import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function RdvIClosed() {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => setShowContent(true), 100);

    // Widget script is already loaded globally via index.html
    // No need to inject it again here

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com';
    const scriptId = 'jsonld-rdv-iclosed';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.id = scriptId;
    ldScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Réserver un Appel Stratégique | Usine à Leads',
      description: "Réservez votre appel stratégique de 45 minutes avec l'équipe Usine à Leads. Audit gratuit et plan d'action personnalisé pour générer des leads qualifiés.",
      url: `${siteUrl}/rdv-iclosed`,
      inLanguage: 'fr-FR',
      publisher: {
        '@type': 'Organization',
        name: 'Usine à Leads',
        url: siteUrl,
      },
      mainEntity: {
        '@type': 'Event',
        name: 'Appel stratégique 45 minutes - Usine à Leads',
        description: "Analyse de votre business et définition de votre stratégie d'acquisition de leads qualifiés.",
        eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
        eventStatus: 'https://schema.org/EventScheduled',
        organizer: {
          '@type': 'Organization',
          name: 'Usine à Leads',
          url: siteUrl,
        },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock',
          name: 'Appel stratégique gratuit',
        },
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Réserver un appel', item: `${siteUrl}/rdv-iclosed` },
        ],
      },
    });
    document.head.appendChild(ldScript);

    return () => {
      clearTimeout(timer);
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white">
      <title>Réserver un Appel Stratégique Gratuit | Usine à Leads - 45 Minutes Offertes</title>
      <meta name="description" content="Réservez votre appel stratégique de 45 minutes avec l'équipe Usine à Leads. Audit gratuit de votre situation et plan d'action personnalisé pour générer des leads qualifiés." />
      <meta name="keywords" content="réserver appel stratégique, audit gratuit génération leads, consultation marketing digital, rendez-vous qualification leads" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/rdv-iclosed`} />
      <meta property="og:title" content="Réserver un Appel Stratégique Gratuit | Usine à Leads" />
      <meta property="og:description" content="45 minutes pour analyser votre business et définir ensemble votre stratégie d'acquisition de leads qualifiés." />
      <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/rdv-iclosed`} />
      <meta property="og:type" content="website" />

      {/* Navbar */}
      <nav className="w-full py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            className="cursor-pointer"
          >
            <img
              src="https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/512cce5c00ccd36955d2d4e00fd25839.png"
              alt="Usine à Leads"
              className="h-10 w-auto"
              loading="eager"
              decoding="async"
              width="140"
              height="40"
            />
          </a>
        </div>
      </nav>

      {/* Contenu principal */}
      <main
        className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 transition-all duration-700 ${
          showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        {/* Header */}
        <section className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-6">
            <i className="ri-calendar-line text-orange-400 text-sm"></i>
            <span className="text-orange-300 text-sm font-medium">Réservation en ligne</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 leading-tight">
            Réservez votre appel stratégique
          </h1>
          <p className="text-lg sm:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed">
            45 minutes pour analyser votre business et définir ensemble votre stratégie d&apos;acquisition de leads.
          </p>
        </section>

        {/* Reassurance badges */}
        <section className="flex flex-wrap justify-center gap-4 mb-10 sm:mb-12">
          <div className="flex items-center gap-2 bg-white/5 rounded-full px-4 py-2 border border-white/10">
            <i className="ri-time-line text-emerald-400 text-sm"></i>
            <span className="text-white/70 text-sm">45 minutes</span>
          </div>
          <div className="flex items-center gap-2 bg-white/5 rounded-full px-4 py-2 border border-white/10">
            <i className="ri-video-line text-emerald-400 text-sm"></i>
            <span className="text-white/70 text-sm">Visio ou téléphone</span>
          </div>
          <div className="flex items-center gap-2 bg-white/5 rounded-full px-4 py-2 border border-white/10">
            <i className="ri-gift-line text-emerald-400 text-sm"></i>
            <span className="text-white/70 text-sm">100% gratuit &amp; sans engagement</span>
          </div>
          <div className="flex items-center gap-2 bg-white/5 rounded-full px-4 py-2 border border-white/10">
            <i className="ri-shield-check-line text-emerald-400 text-sm"></i>
            <span className="text-white/70 text-sm">Audit offert</span>
          </div>
        </section>

        {/* iClosed Widget */}
        <section className="mb-10 sm:mb-14">
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl shadow-orange-500/10">
            <div
              className="iclosed-widget"
              data-url="https://app.iclosed.io/e/usinealead/rdv-45-min-votre-business-votre-besoin"
              title="RDV 45 min - Votre business, votre besoin"
              style={{ width: '100%', height: '620px' }}
            />
          </div>
        </section>

        {/* Ce que vous allez obtenir */}
        <section className="mb-16 sm:mb-20">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-6 text-white/90">
            Ce que vous obtenez lors de cet appel
          </h2>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-orange-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center mb-4">
                <i className="ri-search-eye-line text-2xl text-orange-400"></i>
              </div>
              <h3 className="font-bold text-base mb-2">Audit de votre situation</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Analyse de vos canaux d&apos;acquisition actuels et identification des leviers de croissance.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-emerald-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center mb-4">
                <i className="ri-roadmap-line text-2xl text-emerald-400"></i>
              </div>
              <h3 className="font-bold text-base mb-2">Plan d&apos;action personnalisé</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Un roadmap concret et actionnable pour lancer votre machine à leads rapidement.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-teal-500/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-lg bg-teal-500/20 flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-2xl text-teal-400"></i>
              </div>
              <h3 className="font-bold text-base mb-2">Projections de résultats</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Des estimations réalistes basées sur votre secteur et vos objectifs de croissance.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center text-xs text-white/40">
          © {new Date().getFullYear()} Usine à Leads. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
}
